import { useEffect, useState } from 'react';
import ReactEDSWrapper from '../../utils/react-eds-wrapper';
import { useLoader } from '../../hooks/useLoader';
import { useSwobNotifications } from '../../hooks/NotificationsProvider';
import { Table, Pagination, Row, Cell, MenuItem } from '@dsi/react-eds';
import api from '../../api/axiosInstance';
import { AxiosError } from 'axios';
import { EiapEndpoint } from './EiapClusterInterfaces';

interface PropsObjects {
  enms: EiapEndpoint[];
  selectEnms: (enms: EiapEndpoint[]) => void;
}

const EiapClusterEnmList = (props: PropsObjects) => {
  const [data, setData] = useState<EiapEndpoint[]>([]);
  const { showLoader, hideLoader } = useLoader();
  const { addNotifications } = useSwobNotifications();
  const [page, setPage] = useState(1);
  const rowsPerPage = 5;
  const selectedCount = data.reduce((acc, data) => {
    if (data.selected) {
      return acc + 1;
    }
    return acc;
  }, 0);

  const validateEnmIsLinked = (enmsLinked: EiapEndpoint[], enmsData: EiapEndpoint[]) => {
    if (enmsLinked.length > 0) {
      enmsLinked.forEach((el) => {
        const found = enmsData.find((ed) => ed.id === el.id);
        if (found) {
          found.selected = true;
        }
      });
    }
    return enmsData;
  };

  const fetchEnms = async () => {
    try {
      showLoader();
      const res = await api.get('/api/enm');
      const enms = validateEnmIsLinked(props.enms, res.data);
      setData(enms);
    } catch (error) {
      // show error
      console.log(error);
      addNotifications([
        {
          title: 'Error',
          icon: 'icon-triangle-warning',
          description:
            error && error instanceof AxiosError
              ? error.response?.data?.message || error.message
              : 'An error occurred fetching Test Collections',
          timestamp: new Date(),
          color: 'red'
        }
      ]);
    } finally {
      setTimeout(() => {
        hideLoader();
      }, 500);
    }
  };

  useEffect(() => {
    fetchEnms();
  }, []);

  const columns = [
    { title: 'Name', attribute: 'name' },
    { title: 'Category', attribute: 'category' },
    { title: 'URL', attribute: 'url' }
  ];

  const handleSelectDeselectAll = (type: boolean) => {
    showLoader();
    const selectedData = data.map((data) => {
      return { ...data, selected: type };
    });
    setData(selectedData);
    props.selectEnms(selectedData.filter((sd) => sd.selected));
    hideLoader();
  };

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const handleSelectedRow = (e: any) => {
    const startIndex = (page - 1) * rowsPerPage;
    const endIndex = page * rowsPerPage;
    const selectedRowsOffset = e.map((i: number) => i + (page - 1) * rowsPerPage);
    const newData = data.map((data, index) => {
      if (index >= startIndex && index < endIndex) {
        return {
          ...data,
          selected: selectedRowsOffset.includes(index)
        };
      }

      return data;
    });
    setData(newData);
    props.selectEnms(newData.filter((sd) => sd.selected));
  };

  return (
    <div className="sm-6">
      <div className="table-top">
        <div className="table-top-left">
          <div className="table-info">
            <span>
              {(page - 1) * rowsPerPage + 1} - {Math.min(page * rowsPerPage, data.length)} of
            </span>
            <span> {data.length} items</span>
            <span>{selectedCount > 0 && ` | ${selectedCount} selected`}</span>
          </div>
        </div>
      </div>

      <ReactEDSWrapper
        component={Table}
        id="enms-table"
        columns={columns}
        multiSelect
        rowActions
        checkboxDropdownItems={[
          <ReactEDSWrapper
            component={MenuItem}
            key="select-all"
            value="select-all"
            disabled={selectedCount === data.length}
            onClick={() => handleSelectDeselectAll(true)}
          >
            Select items from all pages
          </ReactEDSWrapper>,
          <ReactEDSWrapper
            component={MenuItem}
            key="deselect-all"
            value="deselect-all"
            disabled={selectedCount === 0}
            onClick={() => handleSelectDeselectAll(false)}
          >
            Deselect items from all pages
          </ReactEDSWrapper>
        ]}
        onSelect={(e) => handleSelectedRow(e)}
      >
        {data.map((row) => (
          <ReactEDSWrapper component={Row} key={row.name} selected={row.selected}>
            {columns.map(({ attribute }) => (
              <ReactEDSWrapper component={Cell} key={attribute}>
                {row[attribute]}
              </ReactEDSWrapper>
            ))}
          </ReactEDSWrapper>
        ))}
      </ReactEDSWrapper>
      <ReactEDSWrapper
        component={Pagination}
        numPages={Math.ceil(data.length / rowsPerPage)}
        selectedPage={page}
        onPaginationChangePage={setPage}
      />
    </div>
  );
};
export default EiapClusterEnmList;
